import React, { useState, useEffect, useId, useMemo } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Typography } from '@mui/material';
import { useTheme, useMediaQuery } from "@mui/material";
import { FONT_COLOR } from '../const/general'
import Progress from '../item/progress'
import { setChronologyLevelColor } from '../func/color';

export default function PieChartSample(props) {
    const [isNoData, setIsNoData] = useState(false);

    const chartId = useId();
    const series = props.valueData ?? [];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const chartHeight = isMobile ? 200 : 300;

    // ラベルから色配列を作る（fallback を用意）
    const pieColors = useMemo(() => {
        const labels = props.labelData;
        if (!Array.isArray(labels) || labels.length === 0) return;
        return labels.map(name => {
            return setChronologyLevelColor(name);
        });
    }, [props.labelData]);

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    // データ更新が完了したらローディング終了
    useEffect(() => {
        loadingEnd();
    }, [props.valueData, props.labelData]);

    // 定期監視用
    useEffect(() => {
        if (props.valueData === null ||
            props.valueData === undefined ||
            props.valueData.length === 0 ||
            props.labelData === null ||
            props.labelData.length === 0 ||
            props.labelData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        setIsNoData(false)
        // 依存関係の「dateArray」は削除しないでください。
        // これがないとprops.valueDataに変化がない場合にローディングが消えなくなるので
    }, [props.dateArray, props.valueData, props.labelData]);

    useEffect(() => {
        if (props.valueData === null ||
            props.valueData === undefined ||
            props.valueData.length === 0 ||
            props.labelData === null ||
            props.labelData.length === 0 ||
            props.labelData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        setIsNoData(false)

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    const options = {
        chart: {
            type: "pie",
            background: 'transparent',
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    try {
                        const idx = config?.dataPointIndex;
                        if (typeof idx !== 'number' || idx < 0) return;

                        // setReady は遅延して呼ぶ（ApexCharts の内部処理が終わるまで待つ）
                        setTimeout(() => {
                            if (typeof props.setReady === 'function') {
                                props.setReady(prev => {
                                    const updated = Object.fromEntries(
                                        Object.keys(prev).map(key => [key, false])
                                    );
                                    return updated;
                                });
                            }
                        }, 0);

                        const label = Array.isArray(props.labelData) ? (props.labelData[idx] ?? '-') : '-';

                        // chronologyList からラベルに一致するレコードを取得
                        const matched = (Array.isArray(props.chronologyList) ? props.chronologyList : [])
                            .filter(item => String(item.TXT_MAIN_DAMAGE_NAME || item.txt_main_damage_name) === String(label));

                        // マップ中心・選択
                        if (matched.length > 0) {
                            // 座標リスト（有効な座標のみ）
                            const coordsList = matched
                                .filter(m => m.dc_lng_num != null && m.dc_lat_num != null)
                                .map(m => [m.dc_lng_num, m.dc_lat_num]);

                            // マップ中心は有効な座標の先頭にフォーカス（必要ならここを平均・bounds に変更）
                            if (coordsList.length > 0 && typeof props.setRiskMapCenter === 'function') {
                                props.setRiskMapCenter(coordsList[0]);
                            }

                            // メッシュ(code)を全件セット（ユニーク）
                            if (typeof props.setSelectMsh === 'function') {
                                const mshCodes = [...new Set(matched.map(m => m.msh_code).filter(Boolean))];
                                if (mshCodes.length > 0) {
                                    props.setSelectMsh(mshCodes);
                                    props.setSelectLabelChronology(label);
                                }
                            }
                            if (typeof props.setSelectMsh === 'function' && label != null) {
                                props.setSelectLabelChronology(label);
                            }
                        }
                    } catch (e) {
                        console.warn('pie click handler error:', e);
                    }
                }
            }
        },
        labels: props.labelData,
        colors: pieColors,
        stroke: {
            show: true,
            width: 0,
            colors: ['transparent']
        },
        legend: {
            position: "top",
            labels: {
                colors: FONT_COLOR
            }
        },
        dataLabels: {
            enabled: true,
            formatter: function (val, opts) {
                // パーセント表示
                return `${parseFloat(val).toFixed(2)}%`;
            }
        },
        tooltip: {
            enabled: true,
            custom: function ({ series, seriesIndex, w }) {
                const val = series[seriesIndex] ?? 0;
                const labels = w.config.labels || [];
                const label = labels[seriesIndex] || '-';
                const total = (w.config.series || []).reduce((s, n) => s + (Number(n) || 0), 0) || 0;
                const percent = total ? ((val / total) * 100).toFixed(2) : '0.00';
                return `
                    <div style="padding:8px;color:#000;background:#fff;border-radius:4px;font-size:12px;line-height:1.4;">
                        <div>被害件数：<strong>${val}件（${percent}%）</strong></div>
                        <div>被害名：<strong>${label}</strong></div>
                    </div>
                `;
            }
        }
    };

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (series && series.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    return (
        <>
            {!props.isReadyFlg ?
                <Progress />
                :
                isNoData ?
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'white'
                        }}
                    >
                        対象データがありません
                    </Typography>
                    :
                    <Chart
                        id={chartId}
                        options={options}
                        series={series}
                        type="pie"
                        height={chartHeight}
                        width="100%"
                        style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                    />
            }
        </>
    );
}